import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { TaskMasterSet, TaskMasterSetForm, TaskMasterSetSearchForm } from 'entities/task_master_set'

type TaskMasterSetsResponse = PagingResponse & {
    results: TaskMasterSet[]
}

export function useFetchTaskMasterSetsApi(searchForm?: Form<TaskMasterSetSearchForm>): IndexApiSet<TaskMasterSetsResponse> & { execute: () => void } {
    const apiPath = 'task_master_sets/'
    const api = useIndexApi<TaskMasterSetsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchTaskMasterSetApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `task_master_sets/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostTaskMasterSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskMasterSetForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TaskMasterSetForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TaskMasterSetForm>) => {
        const apiPath = `task_master_sets/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTaskMasterSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskMasterSetForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskMasterSetForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TaskMasterSetForm>) => {
        const apiPath = `task_master_sets/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTaskMasterSetsApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskMasterSetForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskMasterSetForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TaskMasterSetForm[]>) => {
        const apiPath = `task_master_sets/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTaskMasterSetApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `task_master_sets/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
